import { useEffect, useRef, useState } from 'react';

// Generic data-fetching hook: runs `fetcher` (a function returning a promise)
// whenever `deps` change, tracks loading/error state, and ignores results from
// superseded or unmounted calls. Replaces the hand-rolled
// `let active = true` + isReady/error boilerplate repeated across components.
//
//   const { data, isReady, error } = useHttpData(() => api.get_x(id), [id]);
const useHttpData = (fetcher, deps = [], initialData = null) => {
    const [data, setData] = useState(initialData);
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState(false);
    // Captured once so error resets don't depend on a possibly-unstable prop.
    const initialRef = useRef(initialData);

    useEffect(() => {
        let active = true;
        setIsReady(false);
        setError(false);
        Promise.resolve()
            .then(fetcher)
            .then(result => {
                if (active) {
                    setData(result);
                    setIsReady(true);
                }
            })
            .catch(() => {
                if (active) {
                    setData(initialRef.current);
                    setError(true);
                    setIsReady(true);
                }
            });
        return () => {
            active = false;
        };
        // The caller controls invalidation via `deps`; `fetcher` is captured per run.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return { data, isReady, error };
};

export default useHttpData;
